import React, {useEffect, useState, useRef} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {
  selectAnnouncements,
  selectAnnouncementLoading,
  dismissAnnouncement,
  getNews,
  loadDismissedIds,
} from '../../../app/shared/redux/AnnouncementRedux'
import {AnnouncementModel} from '../../../app/shared/models/AnnouncementModel'

const AnnouncementBar: React.FC = () => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const announcements = useSelector(selectAnnouncements) as AnnouncementModel[]
  const isLoading = useSelector(selectAnnouncementLoading)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isVisible, setIsVisible] = useState(false)
  const [shouldAnimate, setShouldAnimate] = useState(false)
  const messageRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [needsScrolling, setNeedsScrolling] = useState(false)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  // Load dismissed IDs and fetch news on mount
  useEffect(() => {
    dispatch(loadDismissedIds())
    dispatch(getNews())
  }, [dispatch])

  // Reload announcements when language changes
  useEffect(() => {
    dispatch(getNews())
  }, [dispatch, intl.locale])

  // Filter out dismissed announcements
  const activeAnnouncements = announcements.filter(announcement => !announcement.isDismissed)
  const currentAnnouncement = activeAnnouncements[currentIndex] || null

    // Handle announcement rotation and display
  useEffect(() => {
    const intervalId = intervalRef.current
    
    // Clear existing timers
    if (intervalId) clearInterval(intervalId)

    // If no active announcements or still loading, hide
    if (!activeAnnouncements.length || isLoading) {
      setIsVisible(false)
      setShouldAnimate(false)
      return
    }

    // Show the component initially
    setIsVisible(true)
    setShouldAnimate(true)
    
    // If only one announcement, show it permanently
    if (activeAnnouncements.length === 1) {
      setCurrentIndex(0)
      return
    }

    // Multiple announcements - setup rotation
    const rotateToNext = () => {
      // Just change to next announcement - no hiding/showing
      setCurrentIndex(prev => (prev + 1) % activeAnnouncements.length)
    }
    
    // Set up rotation interval
    intervalRef.current = setInterval(rotateToNext, 10000) // Every 10 seconds

    // Cleanup
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [activeAnnouncements.length, isLoading])

  // Check if text needs scrolling
  useEffect(() => {
    if (messageRef.current && containerRef.current && currentAnnouncement) {
      const messageWidth = messageRef.current.scrollWidth
      const containerWidth = containerRef.current.clientWidth - 96 // Account for icon (24px) + close button (24px) + padding (48px)
      setNeedsScrolling(messageWidth > containerWidth)
    }
  }, [currentAnnouncement])

  const handleDismiss = () => {
    if (currentAnnouncement) {
      // Add to localStorage dismissed list
      const dismissedList = JSON.parse(localStorage.getItem('dismissedAnnouncements') || '[]')
      if (!dismissedList.includes(currentAnnouncement.id)) {
        dismissedList.push(currentAnnouncement.id)
        localStorage.setItem('dismissedAnnouncements', JSON.stringify(dismissedList))
      }

      // Animate out
      setShouldAnimate(false)
      setTimeout(() => {
        // Dismiss via Redux
        dispatch(dismissAnnouncement({id: currentAnnouncement.id, permanent: true}))
        
        // Move to next announcement if multiple exist
        const remainingAnnouncements = activeAnnouncements.filter(a => a.id !== currentAnnouncement.id)
        if (remainingAnnouncements.length > 0) {
          // Adjust current index if needed
          const nextIndex = currentIndex >= remainingAnnouncements.length ? 0 : currentIndex
          setCurrentIndex(nextIndex)
          
          // Show next announcement after a brief delay
          setTimeout(() => {
            setIsVisible(true)
            setShouldAnimate(true)
          }, 200)
        } else {
          // No more announcements
          setIsVisible(false)
        }
      }, 300)
    }
  }

  const handleMessageClick = () => {
    if (!currentAnnouncement?.actionUrl) return
    
    try {
      // Validate URL
      new URL(currentAnnouncement.actionUrl)
      window.open(currentAnnouncement.actionUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.warn('Invalid URL:', currentAnnouncement.actionUrl)
    }
  }

  if (!isVisible || !currentAnnouncement) {
    return null
  }

  const {message, type = 'info' as const, dismissible = true} = currentAnnouncement

  const getBarStyles = () => {
    const baseStyles = {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      width: '100%',
      zIndex: 9999,
      padding: '12px 16px',
      fontSize: '14px',
      fontWeight: '500',
      display: 'flex',
      height: '48px',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      transform: shouldAnimate ? 'translateY(0)' : 'translateY(-100%)',
      transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s ease, background-color 0.6s ease, color 0.6s ease',
      opacity: shouldAnimate ? 1 : 0,
      overflow: 'hidden',
    }

    const typeStyles = {
      info: {
        backgroundColor: '#3b82f6',
        color: '#ffffff',
      },
      warning: {
        backgroundColor: '#f59e0b',
        color: '#ffffff',
      },
    }

    return {...baseStyles, ...typeStyles[type]}
  }

  const getIconForType = () => {
    switch (type) {
      case 'info':
        return 'fas fa-info-circle'
      case 'warning':
        return 'fas fa-exclamation-triangle'
      default:
        return 'fas fa-info-circle'
    }
  }

  const getScrollingTextStyles = () => {
    if (!needsScrolling) return {}
    
    return {
      animation: 'scrollText 15s linear infinite',
      whiteSpace: 'nowrap' as const,
    }
  }

  return (
    <>
      <style>
        {`
          @keyframes scrollText {
            0% {
              transform: translateX(100%);
            }
            100% {
              transform: translateX(-100%);
            }
          }
          
          .announcement-message-container {
            overflow: hidden;
            flex: 1;
            display: flex;
            justify-content: center;
            align-items: center;
            text-align: center;
          }
          
          .announcement-message {
            display: inline-block;
            transition: all 0.6s ease;
          }
          
          .announcement-bar-clickable {
            cursor: pointer;
            transition: opacity 0.2s ease;
          }
          
          .announcement-bar-clickable:hover {
            opacity: 0.9;
          }

          .announcement-icon-left {
            width: 24px;
            display: flex;
            justify-content: center;
            align-items: center;
            transition: color 0.6s ease;
          }

          .announcement-close-right {
            width: 24px;
            display: flex;
            justify-content: center;
            align-items: center;
          }

          .announcement-close-right button {
            transition: color 0.6s ease, background-color 0.2s ease !important;
          }
        `}
      </style>
      
      <div style={getBarStyles()} ref={containerRef}>
        <div className="announcement-icon-left">
          <i className={`${getIconForType()} fs-6`}></i>
        </div>
        
        <div 
          className={`announcement-message-container ${
            currentAnnouncement.actionUrl ? 'announcement-bar-clickable' : ''
          }`}
          onClick={currentAnnouncement.actionUrl ? handleMessageClick : undefined}
          title={currentAnnouncement.actionUrl ? 'Click to open link' : undefined}
        >
          <div 
            ref={messageRef}
            className="announcement-message"
            style={getScrollingTextStyles()}
          >
            {message}
          </div>
        </div>

        <div className="announcement-close-right">
          {dismissible && (
            <button
              className='btn btn-sm btn-icon'
              onClick={handleDismiss}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'inherit',
                padding: '4px 8px',
                borderRadius: '4px',
                transition: 'background-color 0.2s ease',
                minWidth: 'auto',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent'
              }}
              title={intl.formatMessage({id: 'SYSTEM.CLOSE'})}
            >
              <i className='fas fa-times fs-7'></i>
            </button>
          )}
        </div>
      </div>
      
      {/* Spacer div to prevent content from being hidden under the fixed bar */}
      <div 
        style={{
          height: shouldAnimate ? '48px' : '0px',
          width: '100%',
          transition: 'height 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      />
    </>
  )
}

export {AnnouncementBar}
